import { useQuery } from '@tanstack/react-query';
import { Clock, Inbox, ListTodo, PlusCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api';
import { getPersonalDashboardApi, type PersonalIssueRow } from '@/lib/discovery-api';
import { useAuthStore } from '@/stores/auth-store';

function IssueList({
  items,
  orgSlug,
  emptyLabel,
}: {
  items: PersonalIssueRow[];
  orgSlug: string;
  emptyLabel: string;
}) {
  if (items.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-xs italic text-muted-foreground">{emptyLabel}</p>
    );
  }
  return (
    <ul className="divide-y">
      {items.slice(0, 8).map((issue) => (
        <li key={issue.id} className="flex items-center justify-between gap-3 p-2 text-sm">
          <Link
            to={`/o/${orgSlug}/projects/${issue.projectKey}/issues/${issue.key}`}
            className="flex min-w-0 items-center gap-2"
          >
            <span className="font-mono text-xs text-primary">{issue.key}</span>
            <span className="truncate">{issue.title}</span>
          </Link>
          <Badge variant="outline" className="shrink-0">
            {issue.statusName}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

export function OrgDashboardPage() {
  const { orgSlug } = useParams();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);

  const query = useQuery({
    queryKey: ['org', activeOrgId, 'dashboard', 'personal'],
    queryFn: () => (activeOrgId ? getPersonalDashboardApi(activeOrgId) : Promise.resolve(null)),
    enabled: Boolean(activeOrgId),
  });

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <PageHeader
        title="Dashboard"
        description={`Your personal view of issues and sprints in ${orgSlug ?? 'this organization'}.`}
      />

      {query.isError ? (
        <EmptyState
          title="Could not load dashboard"
          description={extractApiErrorMessage(query.error)}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Inbox className="h-4 w-4" aria-hidden /> Assigned to me
              </CardTitle>
              <Badge variant="secondary">{query.data?.assignedToMe.length ?? 0}</Badge>
            </CardHeader>
            <CardContent className="p-0">
              {query.isLoading ? (
                <Skeleton className="h-32" />
              ) : (
                <IssueList
                  items={query.data?.assignedToMe ?? []}
                  orgSlug={orgSlug ?? ''}
                  emptyLabel="No open issues assigned to you."
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <PlusCircle className="h-4 w-4" aria-hidden /> Created by me
              </CardTitle>
              <Badge variant="secondary">{query.data?.createdByMe.length ?? 0}</Badge>
            </CardHeader>
            <CardContent className="p-0">
              {query.isLoading ? (
                <Skeleton className="h-32" />
              ) : (
                <IssueList
                  items={query.data?.createdByMe ?? []}
                  orgSlug={orgSlug ?? ''}
                  emptyLabel="You haven't created any issues yet."
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <ListTodo className="h-4 w-4" aria-hidden /> Recently updated
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {query.isLoading ? (
                <Skeleton className="h-32" />
              ) : (
                <IssueList
                  items={query.data?.recentlyUpdated ?? []}
                  orgSlug={orgSlug ?? ''}
                  emptyLabel="No recent activity yet."
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" aria-hidden /> Upcoming sprint end dates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {query.isLoading ? (
                <Skeleton className="h-32" />
              ) : (query.data?.upcomingSprintEnds.length ?? 0) === 0 ? (
                <p className="px-3 py-6 text-center text-xs italic text-muted-foreground">
                  No planned or active sprints with an end date.
                </p>
              ) : (
                <ul className="divide-y">
                  {query.data!.upcomingSprintEnds.map((sprint) => (
                    <li
                      key={sprint.id}
                      className="flex items-center justify-between gap-3 p-2 text-sm"
                    >
                      <Link
                        to={`/o/${orgSlug}/projects/${sprint.projectKey}/backlog`}
                        className="flex min-w-0 items-center gap-2"
                      >
                        <span className="font-mono text-xs text-primary">{sprint.projectKey}</span>
                        <span className="truncate">{sprint.name}</span>
                      </Link>
                      <div className="flex shrink-0 items-center gap-2 text-xs">
                        <Badge variant={sprint.state === 'ACTIVE' ? 'success' : 'outline'}>
                          {sprint.state}
                        </Badge>
                        <span className="text-muted-foreground">
                          {new Date(sprint.endDate).toLocaleDateString()}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
